/*--external-- */
import _ from "lodash";
import React, { useCallback, useMemo, useRef, useState } from "react";
import classNames from "classnames";

/*--internal--*/
import { Button, Tag } from "@toddle-edu/ds-web";
import KonvaCanvas from "UIComponents/KonvaCanvas";
import UIModal from "UIComponents/UIModal";
import { generateRandomId, generateRandomNumber } from "Utils";
import { EyeHiddenOutlined, EyeOutlined } from "@toddle-edu/ds-icons";

/**--relative--*/
import classes from "./Labelling.scss";
import { LABELLING_ITEM_TYPE } from "./LabellingConstant";
import ElementContextMenu from "../ElementContextMenu";
import DragAndDropCanvasHeader from "../../DragAndDropField/DragAndDropCanvasHeader/DragAndDropCanvasHeader";
import {
  stableOrderizeData,
  getInitPropsForNewShape,
  getDragAndDropCanvasWidth,
  sanitizeLabellingQuestionData,
} from "./LabellingUtils";

/**
 *
 * @param {*} props
 * @returns Canvas Header and Canvas container
 */
const Labelling = props => {
  const {
    t,
    data,
    mode,
    config,
    viewSolution,
    answers = {},
    onUpdateData = () => {},
    isFullscreen = false,
    labellingAnswer = {},
    setViewSolution = () => {},
    onToggleFullscreen = () => {},
    worksheetMode,
    isFromInternalTool,
  } = props;

  const dataRef = useRef([]);
  const [drawMode, setDrawMode] = useState({});
  const fieldId = _.get(config, "fieldId", "");

  /**
   * Add new item into data and redraw
   */
  const handleAddNewItem = useCallback(
    item => {
      setDrawMode({});
      const itemKeys = _.split(_.get(item, "key"), "#");
      const category = _.first(itemKeys);
      const type = _.get(itemKeys, "1", LABELLING_ITEM_TYPE.LABEL);
      const canvasId = `konva-canvas-container-id-${fieldId}-${mode}`;
      const payload = { t, type, item, category, canvasId };
      const newItem = getInitPropsForNewShape(payload);
      const updatedData = stableOrderizeData([...dataRef.current, newItem]);
      onUpdateData(updatedData);
    },
    [mode, fieldId]
  );

  /**
   * Update data on change in canvas
   */
  const handleItemsChange = useCallback(
    updatedItem => {
      dataRef.current = _.map(dataRef.current, item => {
        if (_.isEqual(_.get(updatedItem, "id"), _.get(item, "id"))) {
          return {
            ...(_.isObject(item) ? item : {}),
            ...(_.isObject(updatedItem) ? updatedItem : {}),
          };
        } else {
          return item;
        }
      });
      const updatedData = stableOrderizeData(dataRef.current);
      onUpdateData(updatedData);
    },
    [mode, onUpdateData]
  );

  /**
   * Remove item from data and redraw
   */
  const handleItemRemove = useCallback(
    id => {
      // * Remove element or arrows
      const isNot = item => _.get(item, "id") !== id;
      const updatedData = _.map(_.filter(dataRef.current, isNot), element => {
        if (_.has(element, "arrows")) {
          element.arrows = _.filter(_.get(element, "arrows"), isNot);
        }
        return element;
      });
      onUpdateData(updatedData);
    },
    [mode]
  );

  /**
   * Order element on canvas - zIndex management
   */
  const handleElementOrder = useCallback(
    (elementId, orderType) => {
      const elements = stableOrderizeData(_.clone(dataRef.current || []));
      const index = _.findIndex(elements, { id: elementId });
      const category = _.get(elements, [index, "category"]);
      const elementsCount = _.get(_.countBy(elements, { category }), "true", 0);

      // * no ordering: if, no elements or elements is less than 2
      if (index < 0 || !_.isArray(elements) || elementsCount < 2) return;

      const minIndex = _.findIndex(elements, { category });
      const maxIndex = minIndex + elementsCount - 1;
      const newIndex = orderType === "DOWN" ? index - 1 : index + 1;

      if (newIndex < minIndex || newIndex > maxIndex) return;

      // * removing from current position
      const elementToMove = _.first(elements.splice(index, 1));
      if (!elementToMove) return;

      // * inserting element to new position
      elements.splice(newIndex, 0, elementToMove);
      onUpdateData(elements);
    },
    [mode]
  );

  /**
   * On duplicating element update id and add new element
   */
  const handleDuplicateItem = useCallback(
    id => {
      const item = _.find(dataRef.current, { id });
      if (!item) return;

      const newItem = _.cloneDeep(item);
      newItem.id = generateRandomId();
      if (_.has(newItem, "x")) newItem.x = generateRandomNumber(10, 100);
      if (_.has(newItem, "y")) newItem.y = generateRandomNumber(10, 100);
      if (_.has(newItem, "arrows")) newItem.arrows = [];

      const updatedData = stableOrderizeData([...dataRef.current, newItem]);
      onUpdateData(updatedData);
    },
    [mode]
  );

  /**
   * Show context menu for shapes in create mode
   */
  const elementContextMenu = useCallback(
    node => (
      <ElementContextMenu
        node={node}
        onRemove={handleItemRemove}
        onOrderElement={handleElementOrder}
      />
    ),
    [handleItemRemove, handleElementOrder]
  );

  /**
   * Memoized canvas component
   */
  const canvasComponent = useMemo(() => {
    dataRef.current = data || [];

    // * canvas width for different mode
    const canvasWidth = getDragAndDropCanvasWidth(isFullscreen);

    // * updating json data to required formate for canvas
    const canvasData = sanitizeLabellingQuestionData(_.cloneDeep(data), {
      mode,
      drawMode,
    });

    return (
      <KonvaCanvas
        mode={mode}
        key={fieldId}
        fieldId={fieldId}
        data={canvasData}
        width={canvasWidth}
        drawMode={drawMode}
        onRemove={handleItemRemove}
        worksheetMode={worksheetMode}
        contextMenu={elementContextMenu}
        onDuplicate={handleDuplicateItem}
        type={_.get(config, "fieldKey", "")}
        onChange={handleItemsChange}
        options={{
          viewSolution,
          labellingAnswer,
          studentAnswers: answers,
          isSubmitted: _.get(config, "isSubmitted", false),
          showViewSolutionButton: _.get(
            config,
            "showViewSolutionButton",
            false
          ),
          isTeacherStudentViewMode: _.get(
            config,
            "isTeacherStudentViewMode",
            false
          ),
        }}
      />
    );
  }, [
    mode,
    config,
    answers,
    drawMode,
    viewSolution,
    isFullscreen,
    labellingAnswer,
    JSON.stringify(data),
  ]);

  if (isFullscreen) {
    return (
      <UIModal
        isFullscreen
        isOpen={isFullscreen}
        modalContent={classes.modalContent}
        onRequestClose={onToggleFullscreen}
        testId="worksheet-labelling-closeModal-uiModal"
      >
        <DragAndDropCanvasHeader
          t={t}
          mode={mode}
          config={config}
          isFullscreen={isFullscreen}
          onAddNewItem={handleAddNewItem}
          onToggleFullscreen={onToggleFullscreen}
          worksheetMode={worksheetMode}
          isFromInternalTool={isFromInternalTool}
        />
        <div className={classes.canvasContainer}>
          <div style={{ position: "relative" }}>{canvasComponent}</div>
        </div>
      </UIModal>
    );
  }

  const showViewSolutionTag =
    viewSolution && _.get(config, "showViewSolutionButton");
  const containerClassName = classNames(classes.container, {
    [classes.containerViewSolution]: showViewSolutionTag,
  });

  return (
    <>
      <div className={containerClassName}>
        <DragAndDropCanvasHeader
          t={t}
          mode={mode}
          config={config}
          isFullscreen={isFullscreen}
          onAddNewItem={handleAddNewItem}
          onToggleFullscreen={onToggleFullscreen}
          worksheetMode={worksheetMode}
          isFromInternalTool={isFromInternalTool}
        />
        <div className={classes.canvasContainer}>{canvasComponent}</div>
        {showViewSolutionTag && (
          <div className={classes.viewSolutionTagWrapper}>
            <Tag
              _legacy_size="x-small"
              size="small"
              color="teal"
              dsVersion="2.0"
            >
              {t("worksheet:viewing_solution")}
            </Tag>
          </div>
        )}
      </div>
      {/* View solution will be visible for teacher evaluation mode to view correct answer mode */}
      {_.get(config, "showViewSolutionButton") && (
        <div className={classes.viewSolutionBtnWrapper}>
          <Button
            size="medium"
            variant="neutral"
            dsVersion="2.0"
            type="outlined"
            _legacy_size="small"
            _legacy_variant="outlined"
            icon={viewSolution ? <EyeHiddenOutlined /> : <EyeOutlined />}
            onClick={() => setViewSolution(prev => !prev)}
          >
            {t(
              viewSolution
                ? "worksheet:hide_solution"
                : "worksheet:view_solution"
            )}
          </Button>
        </div>
      )}
    </>
  );
};

export default Labelling;

Labelling.defaultProps = {};

Labelling.propTypes = {};
